import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import { FiEdit2, FiX, FiSave, FiTrash2, FiPlus, FiExternalLink } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const ADS_TYPE_OPTIONS = [
  { value: 1, label: "Facebook Only" },
  { value: 2, label: "Google Only" },
  { value: 3, label: "Fallback" },
  { value: 4, label: "Qureka Only" },
];

const ADS_TYPE_COLOR = {
  1: "bg-[#ffffe0] text-[#999900]",
  2: "bg-red-50 text-red-500",
  3: "bg-amber-50 text-amber-600",
  4: "bg-purple-50 text-purple-600",
};

/* ─── Ads Platform Settings Section ─── */
const AdsPlatformSection = ({ token }) => {
  const [adsList, setAdsList]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [editingAd, setEditingAd] = useState(null);
  const [editForm, setEditForm]   = useState({ isAdsRun: true, adsType: 1 });
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${baseUrl}/setting/ads-setting`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await res.json();
        if (res.ok) {
          const data = result.data;
          setAdsList(Array.isArray(data) ? data : data ? [data] : []);
        } else {
          setError(result.message || "Failed to fetch ads settings");
        }
      } catch {
        setError("Network error");
      } finally {
        setLoading(false);
      }
    };
    fetch_();
  }, [token]);

  const openEdit = (ad) => {
    setEditingAd(ad);
    setEditForm({ isAdsRun: ad.isAdsRun, adsType: ad.adsType });
  };

  const closeEdit = () => {
    setEditingAd(null);
    setEditForm({ isAdsRun: true, adsType: 1 });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${baseUrl}/setting/ads-setting/${editingAd.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ isAdsRun: editForm.isAdsRun, adsType: editForm.adsType }),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success("Ads settings updated successfully!");
        setAdsList((prev) => prev.map((item) => (item.id === editingAd.id ? { ...item, ...editForm } : item)));
        closeEdit();
      } else {
        toast.error(result.message || "Failed to update");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="w-6 h-6 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="m-5 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
      ) : adsList.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">No ads configuration found.</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {adsList.map((ad) => (
            <div key={ad.id} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition">
              <div className="flex items-center gap-3">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${ADS_TYPE_COLOR[ad.adsType] || "bg-gray-100 text-gray-500"}`}>
                  {ADS_TYPE_OPTIONS.find((o) => o.value === ad.adsType)?.label || `Type ${ad.adsType}`}
                </span>
                <span className="text-xs text-gray-400">adsType: {ad.adsType}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-xs font-medium ${ad.isAdsRun ? "text-green-500" : "text-gray-400"}`}>
                  {ad.isAdsRun ? "Enabled" : "Disabled"}
                </span>
                <button
                  onClick={() => openEdit(ad)}
                  className="flex items-center gap-1 text-xs text-amber-500 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition"
                >
                  <FiEdit2 size={13} /> Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingAd && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeEdit}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-800">Edit Ads Settings</h3>
              <button onClick={closeEdit} className="text-gray-400 hover:text-gray-600"><FiX size={18} /></button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Ads Type</label>
                <select
                  value={editForm.adsType}
                  onChange={(e) => setEditForm({ ...editForm, adsType: Number(e.target.value) })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-white"
                >
                  {ADS_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500">Is Ads Run</label>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${editForm.isAdsRun ? "text-green-500" : "text-gray-400"}`}>
                    {editForm.isAdsRun ? "Enabled" : "Disabled"}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, isAdsRun: !editForm.isAdsRun })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${editForm.isAdsRun ? "bg-[#ffff00]" : "bg-gray-200"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${editForm.isAdsRun ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium py-2.5 rounded-lg transition"
              >
                <FiSave size={14} />
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button onClick={closeEdit} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─── Ads Link Section ─── */
const AdsLinkSection = ({ token }) => {
  const [links, setLinks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [newLink, setNewLink]     = useState("");
  const [creating, setCreating]   = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editUrl, setEditUrl]     = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/setting/ads-link`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok) {
        setLinks(result.data || []);
      } else {
        toast.error(result.message || "Failed to fetch ads links");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newLink.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${baseUrl}/setting/ads-link`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ adsLink: newLink.trim() }),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success("Ads link created!");
        setNewLink("");
        fetchLinks();
      } else {
        toast.error(result.message || "Failed to create");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setCreating(false);
    }
  };

  const openEditLink = (link) => {
    setEditingLink(link);
    setEditUrl(link.adsLink);
  };

  const closeEditLink = () => {
    setEditingLink(null);
    setEditUrl("");
  };

  const handleEditSave = async () => {
    if (!editUrl.trim()) return;
    setSavingEdit(true);
    try {
      const res = await fetch(`${baseUrl}/setting/ads-link/${editingLink.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ adsLink: editUrl.trim() }),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success("Ads link updated!");
        setLinks((prev) => prev.map((l) => (l.id === editingLink.id ? { ...l, adsLink: editUrl.trim() } : l)));
        closeEditLink();
      } else {
        toast.error(result.message || "Failed to update");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggle = async (link) => {
    setTogglingId(link.id);
    try {
      const res = await fetch(`${baseUrl}/setting/ads-link/${link.id}/toggle`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(`Link ${link.isActive ? "deactivated" : "activated"}!`);
        setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, isActive: !l.isActive } : l)));
      } else {
        toast.error(result.message || "Failed to toggle");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this ads link?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${baseUrl}/setting/ads-link/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Ads link deleted!");
        setLinks((prev) => prev.filter((l) => l.id !== id));
      } else {
        const result = await res.json();
        toast.error(result.message || "Failed to delete");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      {/* Add link form */}
      <form onSubmit={handleCreate} className="px-5 py-4 border-b border-gray-100">
        <div className="flex gap-3">
          <input
            type="url"
            placeholder="https://example.com/ad"
            value={newLink}
            onChange={(e) => setNewLink(e.target.value)}
            required
            className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
          />
          <button
            type="submit"
            disabled={creating}
            className="flex items-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium px-4 py-2.5 rounded-lg transition whitespace-nowrap"
          >
            <FiPlus size={14} />
            {creating ? "Adding..." : "Add Link"}
          </button>
        </div>
      </form>

      {/* Links list */}
      {loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="w-6 h-6 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : links.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">No ads links yet. Add one above.</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {links.map((link) => (
            <div key={link.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition">
              <div className="flex-1 min-w-0">
                <a
                  href={link.adsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#999900] hover:underline flex items-center gap-1 truncate"
                >
                  <FiExternalLink size={12} className="shrink-0" />
                  <span className="truncate">{link.adsLink}</span>
                </a>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {/* Active toggle */}
                <button
                  disabled={togglingId === link.id}
                  onClick={() => handleToggle(link)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 ${link.isActive ? "bg-[#ffff00]" : "bg-gray-200"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${link.isActive ? "translate-x-6" : "translate-x-1"}`} />
                </button>
                <span className={`text-xs font-medium w-14 ${link.isActive ? "text-green-500" : "text-gray-400"}`}>
                  {link.isActive ? "Active" : "Inactive"}
                </span>
                {/* Edit */}
                <button
                  onClick={() => openEditLink(link)}
                  className="flex items-center gap-1 text-xs text-amber-500 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition"
                >
                  <FiEdit2 size={13} /> Edit
                </button>
                {/* Delete */}
                <button
                  onClick={() => handleDelete(link.id)}
                  disabled={deletingId === link.id}
                  className="flex items-center gap-1 text-xs text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition disabled:opacity-60"
                >
                  <FiTrash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingLink && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeEditLink}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-800">Edit Ads Link</h3>
              <button onClick={closeEditLink} className="text-gray-400 hover:text-gray-600"><FiX size={18} /></button>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Ads Link URL</label>
              <input
                type="url"
                value={editUrl}
                onChange={(e) => setEditUrl(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                placeholder="https://example.com/ad"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleEditSave}
                disabled={savingEdit}
                className="flex-1 flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium py-2.5 rounded-lg transition"
              >
                <FiSave size={14} />
                {savingEdit ? "Saving..." : "Save Changes"}
              </button>
              <button onClick={closeEditLink} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─── Main Export ─── */
const AdsSettings = () => {
  const token = localStorage.getItem("token");

  return (
    <>
      <AdsPlatformSection token={token} />

      {/* Ads Links Section */}
      <div className="mt-0">
        <div className="px-5 py-4 border-t border-b border-gray-100 bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-700">Ads Links</h4>
          <p className="text-xs text-gray-400 mt-0.5">Only one link can be active at a time</p>
        </div>
        <AdsLinkSection token={token} />
      </div>
    </>
  );
};

export default AdsSettings;
